import { useState, useCallback, useRef, useMemo, useEffect } from 'react';
import { toastMessage } from '@ui/components/Toast';
import { useGroupStore } from '@/stores/groupStore';
import { useQueryClient } from '@tanstack/react-query';
import {
    useGetTemplatePage,
    useGetTemplateDetail,
    useGetVersion,
    useCreateTemplate1,
    useUpdateTemplate1,
    useDeleteTemplate1,
    useGetVersionHistoryList,
    useActivateVersion,
} from '@hooks/generated/consent';
import { apiClientManager } from '@app/stores/api-manager';
import { uploadFile } from '@api/@fileUpload';
import type { ConsentTemplate, TemplateFormData, QuestionItem, QuestionType, UploadFileType, ConsentTemplateVersion } from '../types';
import type {
    ConsentTemplateSummaryResponse,
    ConsentTemplateResponse,
    ConsentTemplateVersionSummaryResponse,
} from '@careforu-tailody/tailody-core-api';

// Local type definition (removed from API)
interface ApiQuestionItem {
    questionText?: string;
    type?: 'TEXT' | 'SINGLE_CHOICE' | 'MULTI_CHOICE';
    options?: string[];
    required?: boolean;
    displayOrder?: number;
}
import {
    useSensor,
    useSensors,
    PointerSensor,
    KeyboardSensor,
    DragEndEvent,
} from '@dnd-kit/core';
import { arrayMove, sortableKeyboardCoordinates } from '@dnd-kit/sortable';

const initialFormData: TemplateFormData = {
    name: '',
    content: '',
    questions: [],
    isMandatory: false,
    isFileUploaded: false,
    fileUrl: undefined,
    fileName: undefined,
    fileType: null,
    hasSignatureBox: true,
};

// API QuestionItem을 로컬 QuestionItem으로 변환
function mapApiQuestionToLocal(apiQuestion: ApiQuestionItem, index: number): QuestionItem {
    // API 타입을 로컬 타입으로 매핑
    let type: QuestionType = 'single';
    if (apiQuestion.type === 'TEXT') {
        type = 'text';
    } else if (apiQuestion.type === 'MULTI_CHOICE') {
        type = 'multiple';
    } else {
        type = 'single'; // SINGLE_CHOICE 또는 기본값
    }

    return {
        id: `q-${index}-${Date.now()}`,
        name: apiQuestion.questionText || '',
        type,
        options: (type === 'single' || type === 'multiple') && apiQuestion.options
            ? apiQuestion.options.map((opt, i) => ({ id: `o-${index}-${i}`, value: opt }))
            : [],
        isRequired: apiQuestion.required ?? false,
    };
}

// 로컬 QuestionItem을 API QuestionItem으로 변환
function mapLocalQuestionToApi(question: QuestionItem, index: number): ApiQuestionItem {
    // 로컬 타입을 API 타입으로 매핑
    let apiType: 'TEXT' | 'SINGLE_CHOICE' | 'MULTI_CHOICE' = 'SINGLE_CHOICE';
    if (question.type === 'text') {
        apiType = 'TEXT';
    } else if (question.type === 'multiple') {
        apiType = 'MULTI_CHOICE';
    } else {
        apiType = 'SINGLE_CHOICE';
    }

    return {
        questionText: question.name,
        type: apiType,
        options: (question.type === 'single' || question.type === 'multiple') ? question.options.map(o => o.value) : undefined,
        required: question.isRequired,
        displayOrder: index,
    };
}

// API 템플릿 응답을 로컬 ConsentTemplate으로 변환
function mapApiTemplateToLocal(apiTemplate: ConsentTemplateSummaryResponse): ConsentTemplate {
    const apiAny = apiTemplate as Record<string, unknown>;
    const contentType = apiAny.contentType as string | undefined;
    const isFileUploaded = contentType === 'PDF' || contentType === 'IMAGE';
    const fileType: UploadFileType = contentType === 'PDF' ? 'pdf' : contentType === 'IMAGE' ? 'image' : null;

    return {
        id: apiTemplate.id ?? 0,
        name: apiTemplate.title || '',
        content: '', // summary에는 content가 없음
        questions: [],
        isMandatory: apiTemplate.required ?? false,
        createdAt: apiTemplate.createdAt || '',
        updatedAt: apiTemplate.createdAt || '',
        isFileUploaded,
        fileUrl: apiAny.fileUrl as string | undefined,
        fileType,
        currentVersion: apiTemplate.version,
        activeVersion: apiTemplate.version,
        hasSignatureBox: true,
    };
}

// API 상세 응답을 로컬 ConsentTemplate으로 변환
function mapApiTemplateDetailToLocal(apiTemplate: ConsentTemplateResponse): ConsentTemplate {
    const apiAny = apiTemplate as Record<string, unknown>;
    const contentType = apiAny.contentType as string | undefined;
    const isFileUploaded = contentType === 'PDF' || contentType === 'IMAGE';
    const fileType: UploadFileType = contentType === 'PDF' ? 'pdf' : contentType === 'IMAGE' ? 'image' : null;

    return {
        id: apiTemplate.id ?? 0,
        name: apiTemplate.title || '',
        content: apiTemplate.contentHtml || '',
        questions: (apiTemplate.questions || []).map((q, i) => mapApiQuestionToLocal(q, i)),
        isMandatory: apiTemplate.required ?? false,
        createdAt: apiTemplate.createdAt || '',
        updatedAt: apiTemplate.updatedAt || '',
        isFileUploaded,
        fileUrl: apiTemplate.fileUrl,
        fileType,
        currentVersion: apiTemplate.version,
        activeVersion: apiTemplate.version,
        hasSignatureBox: true,
    };
}

// 페이지 크기 상수
const PAGE_SIZE = 10;

export function useConsentTemplates() {
    const queryClient = useQueryClient();
    const selectedGroup = useGroupStore((state) => state.selectedGroup);
    const groupId = selectedGroup?.id || 0;

    // 페이지 상태
    const [currentPage, setCurrentPage] = useState(0);

    // API 훅으로 템플릿 목록 조회
    const {
        data: templatesData,
        isLoading: isLoadingTemplates,
        refetch: refetchTemplates,
    } = useGetTemplatePage({
        request: {
            groupId,
            pageable: { page: currentPage, size: PAGE_SIZE },
        },
        options: {
            enabled: !!groupId,
        },
    });

    // 페이지 정보
    const pageInfo = useMemo(() => {
        const totalElements = templatesData?.totalElements || 0;
        const totalPages = templatesData?.totalPages || 0;
        const isFirst = templatesData?.first ?? true;
        const isLast = templatesData?.last ?? true;
        return {
            currentPage,
            totalPages,
            totalElements,
            pageSize: PAGE_SIZE,
            isFirst,
            isLast,
            hasNext: !isLast,
            hasPrev: !isFirst,
        };
    }, [templatesData, currentPage]);

    // API 응답을 로컬 타입으로 변환
    const templates = useMemo((): ConsentTemplate[] => {
        const content = (templatesData?.content || []) as ConsentTemplateSummaryResponse[];
        return content.map(mapApiTemplateToLocal);
    }, [templatesData]);

    // 페이지 변경 함수
    const goToPage = useCallback((page: number) => {
        if (page >= 0 && page < (pageInfo.totalPages || 1)) {
            setCurrentPage(page);
        }
    }, [pageInfo.totalPages]);

    const goToNextPage = useCallback(() => {
        if (pageInfo.hasNext) {
            setCurrentPage(prev => prev + 1);
        }
    }, [pageInfo.hasNext]);

    const goToPrevPage = useCallback(() => {
        if (pageInfo.hasPrev) {
            setCurrentPage(prev => prev - 1);
        }
    }, [pageInfo.hasPrev]);

    const goToFirstPage = useCallback(() => {
        setCurrentPage(0);
    }, []);

    const goToLastPage = useCallback(() => {
        if (pageInfo.totalPages > 0) {
            setCurrentPage(pageInfo.totalPages - 1);
        }
    }, [pageInfo.totalPages]);

    // 모달 상태
    const [isTemplateModalOpen, setIsTemplateModalOpen] = useState(false);
    const [isViewModalOpen, setIsViewModalOpen] = useState(false);
    const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
    const [isVersionHistoryModalOpen, setIsVersionHistoryModalOpen] = useState(false);
    const [selectedTemplate, setSelectedTemplate] = useState<ConsentTemplate | null>(null);
    const [templateModalMode, setTemplateModalMode] = useState<'create' | 'edit'>('create');
    const [selectedVersionForPreview, setSelectedVersionForPreview] = useState<ConsentTemplateVersion | null>(null);

    // 폼 상태
    const [templateFormData, setTemplateFormData] = useState<TemplateFormData>(initialFormData);

    // refs
    const fileInputRef = useRef<HTMLInputElement>(null);

    // 선택된 템플릿 상세 조회
    const { data: templateDetailData } = useGetTemplateDetail({
        request: {
            groupId,
            templateId: selectedTemplate?.id ? Number(selectedTemplate.id) : 0,
        },
        options: {
            enabled: !!groupId && !!selectedTemplate?.id,
        },
    });

    // 선택된 템플릿 버전 이력 조회
    const { data: versionHistoryData, refetch: refetchVersionHistory } = useGetVersionHistoryList({
        request: {
            groupId,
            templateId: selectedTemplate?.id ? Number(selectedTemplate.id) : 0,
        },
        options: {
            enabled: !!groupId && !!selectedTemplate?.id && isVersionHistoryModalOpen,
        },
    });

    // 선택된 버전의 상세 정보 조회 (버전 변경시 해당 버전의 내용을 가져옴)
    const { data: selectedVersionData } = useGetVersion({
        request: {
            groupId: groupId || 0,
            templateId: selectedTemplate?.id ? Number(selectedTemplate.id) : 0,
            versionNumber: selectedVersionForPreview?.version || 1,
        },
        options: {
            enabled: !!groupId && !!selectedTemplate?.id && !!selectedVersionForPreview,
        },
    });

    // 버전 이력을 로컬 타입으로 변환
    const versionHistory = useMemo((): ConsentTemplateVersion[] => {
        const versions = (versionHistoryData || []) as ConsentTemplateVersionSummaryResponse[];
        return versions.map((v) => ({
            version: v.versionNumber || 1,
            content: '', // summary에는 content가 없음
            questions: [],
            createdAt: v.createdAt || '',
            createdBy: v.createdBy,
            changeNote: v.changeNote,
        }));
    }, [versionHistoryData]);

    // 폼 데이터가 이미 설정되었는지 추적하는 ref
    const formDataLoadedRef = useRef<number | null>(null);

    // 템플릿 상세 데이터가 로드되면 폼 데이터 업데이트 (수정 모달용)
    useEffect(() => {
        if (
            templateModalMode === 'edit' &&
            isTemplateModalOpen &&
            selectedTemplate &&
            templateDetailData
        ) {
            const detail = templateDetailData as ConsentTemplateResponse;
            // 이미 같은 템플릿에 대해 폼 데이터가 설정되었으면 스킵
            if (detail.id === selectedTemplate.id && formDataLoadedRef.current !== detail.id) {
                formDataLoadedRef.current = detail.id;
                const localTemplate = mapApiTemplateDetailToLocal(detail);
                setTemplateFormData({
                    name: localTemplate.name,
                    content: localTemplate.content,
                    questions: localTemplate.questions,
                    isMandatory: localTemplate.isMandatory,
                    isFileUploaded: localTemplate.isFileUploaded,
                    fileUrl: localTemplate.fileUrl,
                    fileName: undefined,
                    fileType: localTemplate.fileType,
                    hasSignatureBox: localTemplate.hasSignatureBox ?? true,
                });
            }
        }
    }, [templateDetailData, templateModalMode, isTemplateModalOpen, selectedTemplate]);

    // 버전 이력 모달이 열려있을 때 templates가 업데이트되면 selectedTemplate도 업데이트
    useEffect(() => {
        if (isVersionHistoryModalOpen && selectedTemplate && templates.length > 0) {
            const updatedTemplate = templates.find(t => t.id === selectedTemplate.id);
            if (updatedTemplate && updatedTemplate.activeVersion !== selectedTemplate.activeVersion) {
                setSelectedTemplate(updatedTemplate);
            }
        }
    }, [templates, isVersionHistoryModalOpen, selectedTemplate]);


    // Mutations
    const createTemplateMutation = useCreateTemplate1({
        options: {
            onSuccess: () => {
                refetchTemplates();
                toastMessage({ message: '템플릿이 생성되었습니다.', type: 'success' });
                // 성공 시에만 모달 닫기
                setIsTemplateModalOpen(false);
                setSelectedTemplate(null);
                formDataLoadedRef.current = null;
            },
            onError: (error: unknown) => {
                const axiosError = error as { response?: { data?: { data?: Array<{ message?: string }>, message?: string } } };
                console.log('error', error)
                if ((axiosError?.response?.data?.data?.length ?? 0) > 0) {
                    axiosError.response?.data?.data?.forEach((item: { message?: string }) => {
                        toastMessage({ message: item.message || '템플릿 생성에 실패했습니다.', type: 'error' });
                    });
                } else {

                    toastMessage({ message: axiosError.response?.data?.message || '템플릿 생성에 실패했습니다.', type: 'error' });
                }
            },
        },
    });

    const updateTemplateMutation = useUpdateTemplate1({
        options: {
            onSuccess: () => {
                refetchTemplates();
                // 템플릿 상세 및 버전 쿼리 캐시 무효화 (모달 재오픈 시 최신 데이터 조회)
                queryClient.invalidateQueries({ queryKey: ['getTemplateDetail'] });
                queryClient.invalidateQueries({ queryKey: ['getVersion'] });
                refetchVersionHistory();
                toastMessage({ message: '템플릿이 수정되었습니다.', type: 'success' });
                // 성공 시에만 모달 닫기
                setIsTemplateModalOpen(false);
                setSelectedTemplate(null);
                formDataLoadedRef.current = null;
            },
            onError: (error: any) => {
                const axiosError = error as { response?: { data?: { data?: Array<{ message?: string }>, message?: string } } };
                console.log('error', error)
                if ((axiosError?.response?.data?.data?.length ?? 0) > 0) {
                    axiosError.response?.data?.data?.forEach((item: { message?: string }) => {
                        toastMessage({ message: item.message || '템플릿 수정에 실패했습니다.', type: 'error' });
                    });
                }
                else {
                    toastMessage({ message: error?.response?.data?.message || '템플릿 수정에 실패했습니다.', type: 'error' });
                }
            },
        },
    });

    const deleteTemplateMutation = useDeleteTemplate1({
        options: {
            onSuccess: () => {
                refetchTemplates();
                toastMessage({ message: '템플릿이 삭제되었습니다.', type: 'success' });
            },
            onError: (error: any) => {
                toastMessage({ message: error?.response?.data?.message || '템플릿 삭제에 실패했습니다.', type: 'error' });
            },
        },
    });

    const activateVersionMutation = useActivateVersion({
        options: {
            onSuccess: () => {
                refetchTemplates();
                refetchVersionHistory();
                // 템플릿 상세 쿼리 캐시 무효화 (버전 변경 후 미리보기/수정모달에 새 버전 데이터 반영)
                queryClient.invalidateQueries({ queryKey: ['getTemplateDetail'] });
                queryClient.invalidateQueries({ queryKey: ['getVersion'] });
                toastMessage({ message: '사용 버전이 변경되었습니다.', type: 'success' });
            },
            onError: (error: any) => {
                toastMessage({ message: error?.response?.data?.message || '버전 변경에 실패했습니다.', type: 'error' });
            },
        },
    });

    // 드래그 앤 드롭 센서
    const sensors = useSensors(
        useSensor(PointerSensor, {
            activationConstraint: { distance: 8 },
        }),
        useSensor(KeyboardSensor, {
            coordinateGetter: sortableKeyboardCoordinates,
        })
    );

    // 템플릿 생성 모달 열기
    const openCreateModal = useCallback(() => {
        setTemplateModalMode('create');
        setTemplateFormData(initialFormData);
        setIsTemplateModalOpen(true);
    }, []);

    // 템플릿 수정 모달 열기
    const openEditModal = useCallback((template: ConsentTemplate) => {
        setTemplateModalMode('edit');
        setSelectedTemplate(template);

        // 상세 데이터가 있으면 그것을 사용, 없으면 기본값
        const detail = templateDetailData as ConsentTemplateResponse | undefined;
        if (detail && detail.id === template.id) {
            const localTemplate = mapApiTemplateDetailToLocal(detail);
            setTemplateFormData({
                name: localTemplate.name,
                content: localTemplate.content,
                questions: localTemplate.questions,
                isMandatory: localTemplate.isMandatory,
                isFileUploaded: localTemplate.isFileUploaded,
                fileUrl: localTemplate.fileUrl,
                fileName: undefined,
                fileType: localTemplate.fileType,
                hasSignatureBox: localTemplate.hasSignatureBox ?? true,
            });
        } else {
            setTemplateFormData({
                name: template.name,
                content: template.content,
                questions: template.questions,
                isMandatory: template.isMandatory,
                isFileUploaded: template.isFileUploaded,
                fileUrl: template.fileUrl,
                fileName: template.fileName,
                fileType: template.fileType,
                hasSignatureBox: template.hasSignatureBox ?? true,
            });
        }
        setIsTemplateModalOpen(true);
    }, [templateDetailData]);

    // 템플릿 보기 모달 열기
    const openViewModal = useCallback((template: ConsentTemplate) => {
        setSelectedTemplate(template);
        setIsViewModalOpen(true);
    }, []);

    // 삭제 확인 모달 열기
    const openDeleteModal = useCallback((template: ConsentTemplate) => {
        setSelectedTemplate(template);
        setIsDeleteModalOpen(true);
    }, []);

    // 모달 닫기
    const closeModals = useCallback(() => {
        setIsTemplateModalOpen(false);
        setIsViewModalOpen(false);
        setIsDeleteModalOpen(false);
        setIsVersionHistoryModalOpen(false);
        setSelectedTemplate(null);
        setSelectedVersionForPreview(null);
        // 폼 데이터 로드 상태 초기화
        formDataLoadedRef.current = null;
    }, []);

    // 버전 이력 모달 열기
    const openVersionHistoryModal = useCallback((template: ConsentTemplate) => {
        setSelectedTemplate(template);
        setSelectedVersionForPreview(null);
        setIsVersionHistoryModalOpen(true);
    }, []);

    // 버전 이력 모달만 닫기
    const closeVersionHistoryModal = useCallback(() => {
        setIsVersionHistoryModalOpen(false);
        setSelectedVersionForPreview(null);
    }, []);

    // 템플릿 필수/선택 토글
    const toggleTemplateMandatory = useCallback((template: ConsentTemplate) => {
        if (!groupId) return;

        updateTemplateMutation.mutate({
            groupId,
            templateId: template.id,
            consentTemplateUpdateRequest: {
                required: !template.isMandatory,
            } as never,
        });
    }, [groupId, updateTemplateMutation]);

    // 템플릿 복제 - API에서 직접 상세 정보를 조회 후 복제
    const duplicateTemplate = useCallback(async (template: ConsentTemplate) => {
        if (!groupId) return;

        try {
            // API에서 템플릿 상세 정보 직접 조회
            const response = await apiClientManager.apis.consent.getTemplateDetail({
                groupId,
                templateId: Number(template.id),
            });

            const detail = response.data?.data as ConsentTemplateResponse | undefined;
            if (detail) {
                // contentType 결정 (API 응답에서 가져오거나 fileUrl 기반으로 판단)
                const detailAny = detail as Record<string, unknown>;
                const contentType = detailAny.contentType as string | undefined;

                createTemplateMutation.mutate({
                    groupId,
                    consentTemplateCreateRequest: {
                        title: `${detail.title} (복사본)`,
                        contentType: contentType || (detail.fileUrl ? (detail.fileUrl.toLowerCase().endsWith('.pdf') ? 'PDF' : 'IMAGE') : 'HTML'),
                        contentHtml: detail.contentHtml,
                        fileUrl: detail.fileUrl,
                        questions: detail.questions,
                        required: detail.required,
                    } as never,
                });
            } else {
                toastMessage({ message: '템플릿 정보를 불러올 수 없습니다.', type: 'error' });
            }
        } catch (error) {
            console.error('템플릿 복제 실패:', error);
            toastMessage({ message: '템플릿 복제에 실패했습니다.', type: 'error' });
        }
    }, [groupId, createTemplateMutation]);

    // 사용 버전 변경
    const setActiveVersion = useCallback((templateId: string, version: number) => {
        if (!groupId) return;

        activateVersionMutation.mutate({
            groupId,
            templateId: Number(templateId),
            versionNumber: version,
        });
    }, [groupId, activateVersionMutation]);

    // 특정 버전 내용 조회
    const getVersionContent = useCallback((_template: ConsentTemplate, version: number): ConsentTemplateVersion | undefined => {
        return versionHistory.find((v) => v.version === version);
    }, [versionHistory]);

    // 템플릿 삭제
    const deleteTemplate = useCallback(() => {
        if (!selectedTemplate || !groupId) return;

        deleteTemplateMutation.mutate({
            groupId,
            templateId: Number(selectedTemplate.id),
        });
        closeModals();
    }, [selectedTemplate, groupId, deleteTemplateMutation, closeModals]);

    // 템플릿 저장 (생성/수정)
    const saveTemplate = useCallback((changeNote?: string) => {
        if (!templateFormData.name.trim()) {
            toastMessage({ message: '양식명을 입력해주세요.', type: 'warning' });
            return;
        }

        if (!groupId) {
            toastMessage({ message: '그룹을 선택해주세요.', type: 'warning' });
            return;
        }

        // 파일이 업로드되지 않은 경우에만 내용 검증
        if (!templateFormData.isFileUploaded && !templateFormData.content.trim()) {
            toastMessage({ message: '동의서 내용을 입력해주세요.', type: 'warning' });
            return;
        }

        // 질문 유효성 검사
        for (const question of templateFormData.questions) {
            if (!question.name.trim()) {
                toastMessage({ message: '모든 질문의 이름을 입력해주세요.', type: 'warning' });
                return;
            }
            // 선택형 질문인 경우 옵션 검사
            if (question.type === 'single') {
                if (question.options.length < 2) {
                    toastMessage({ message: `"${question.name}" 질문에 최소 2개의 옵션이 필요합니다.`, type: 'warning' });
                    return;
                }
                const emptyOptions = question.options.filter(o => !o.value.trim());
                if (emptyOptions.length > 0) {
                    toastMessage({ message: `"${question.name}" 질문의 모든 옵션을 입력해주세요.`, type: 'warning' });
                    return;
                }
            }
        }

        // 질문을 API 형식으로 변환
        const apiQuestions = templateFormData.questions.map((q, i) => mapLocalQuestionToApi(q, i));

        // 콘텐츠 타입 결정
        const getContentType = () => {
            if (!templateFormData.isFileUploaded) return 'HTML';
            if (templateFormData.fileType === 'pdf') return 'PDF';
            return 'IMAGE';
        };

        if (templateModalMode === 'create') {
            createTemplateMutation.mutate({
                groupId,
                consentTemplateCreateRequest: {
                    title: templateFormData.name,
                    contentType: getContentType(),
                    contentHtml: templateFormData.isFileUploaded ? undefined : templateFormData.content,
                    fileUrl: templateFormData.isFileUploaded ? templateFormData.fileUrl : undefined,
                    questions: apiQuestions.length > 0 ? apiQuestions : undefined,
                    required: templateFormData.isMandatory,
                } as never,
            });
        } else if (selectedTemplate) {
            updateTemplateMutation.mutate({
                groupId,
                templateId: selectedTemplate.id,
                consentTemplateUpdateRequest: {
                    title: templateFormData.name,
                    contentType: getContentType(),
                    contentHtml: templateFormData.isFileUploaded ? undefined : templateFormData.content,
                    fileUrl: templateFormData.isFileUploaded ? templateFormData.fileUrl : undefined,
                    questions: apiQuestions.length > 0 ? apiQuestions : undefined,
                    required: templateFormData.isMandatory,
                    changeNote: changeNote || '내용 수정',
                } as never,
            });
        }
        // 모달 닫기는 mutation의 onSuccess에서 처리됨 (실패 시 모달 유지)
    }, [templateModalMode, selectedTemplate, templateFormData, groupId, createTemplateMutation, updateTemplateMutation]);

    // 파일 업로드 상태
    const [isUploading, setIsUploading] = useState(false);

    // 파일 업로드 (PDF 또는 이미지)
    const handleFileUpload = useCallback(async (e: React.ChangeEvent<HTMLInputElement>) => {
        const file = e.target.files?.[0];
        if (!file) return;

        // 파일 타입 체크
        const validImageTypes = ['image/png', 'image/jpeg', 'image/jpg', 'image/webp'];
        const validPdfType = 'application/pdf';
        const isPdf = file.type === validPdfType;
        const isImage = validImageTypes.includes(file.type);

        if (!isPdf && !isImage) {
            toastMessage({ message: 'PDF 또는 이미지 파일만 업로드 가능합니다. (PDF, PNG, JPG, WEBP)', type: 'warning' });
            return;
        }

        if (file.size > 10 * 1024 * 1024) {
            toastMessage({ message: '파일 크기는 10MB 이하여야 합니다.', type: 'warning' });
            return;
        }

        const fileType: UploadFileType = isPdf ? 'pdf' : 'image';

        // 업로드 상태 표시
        setIsUploading(true);

        try {
            // FileUpload API를 사용하여 서버에 파일 업로드
            const uploadResult = await uploadFile(file);

            if (!uploadResult.success || !uploadResult.fileUrl) {
                throw new Error('파일 업로드 실패');
            }

            setTemplateFormData((prev) => ({
                ...prev,
                isFileUploaded: true,
                fileUrl: uploadResult.fileUrl,
                fileName: file.name,
                fileType,
                hasSignatureBox: true,
                content: '',
                questions: [],
            }));

            toastMessage({ message: isPdf ? 'PDF가 업로드되었습니다.' : '이미지가 업로드되었습니다.', type: 'success' });
        } catch (error) {
            console.error('File upload error:', error);
            toastMessage({ message: '파일 업로드에 실패했습니다. 다시 시도해주세요.', type: 'error' });
        } finally {
            setIsUploading(false);
            if (fileInputRef.current) {
                fileInputRef.current.value = '';
            }
        }
    }, []);

    // 파일 업로드 취소
    const clearFileUpload = useCallback(() => {
        setTemplateFormData((prev) => ({
            ...prev,
            isFileUploaded: false,
            fileUrl: undefined,
            fileName: undefined,
            fileType: null,
            hasSignatureBox: true,
        }));
    }, []);

    // 드래그 앤 드롭 핸들러
    const handleDragEnd = useCallback((event: DragEndEvent) => {
        const { active, over } = event;
        if (over && active.id !== over.id) {
            setTemplateFormData((prev) => {
                const oldIndex = prev.questions.findIndex((q) => q.id === active.id);
                const newIndex = prev.questions.findIndex((q) => q.id === over.id);
                return {
                    ...prev,
                    questions: arrayMove(prev.questions, oldIndex, newIndex),
                };
            });
        }
    }, []);

    // 질문 추가
    const addQuestion = useCallback(() => {
        const newQuestion: QuestionItem = {
            id: `q-${Date.now()}`,
            name: '',
            type: 'single',
            options: [
                { id: `o-${Date.now()}-1`, value: '' },
                { id: `o-${Date.now()}-2`, value: '' },
            ],
            isRequired: false,
        };
        setTemplateFormData((prev) => ({
            ...prev,
            questions: [...prev.questions, newQuestion],
        }));
    }, []);

    // 질문 삭제
    const deleteQuestion = useCallback((questionId: string) => {
        setTemplateFormData((prev) => ({
            ...prev,
            questions: prev.questions.filter((q) => q.id !== questionId),
        }));
    }, []);

    // 질문 수정
    const updateQuestion = useCallback((questionId: string, updates: Partial<QuestionItem>) => {
        setTemplateFormData((prev) => ({
            ...prev,
            questions: prev.questions.map((q) =>
                q.id === questionId ? { ...q, ...updates } : q
            ),
        }));
    }, []);

    // 질문 타입 변경
    const changeQuestionType = useCallback((questionId: string, type: QuestionType) => {
        setTemplateFormData((prev) => ({
            ...prev,
            questions: prev.questions.map((q) =>
                q.id === questionId
                    ? {
                        ...q,
                        type,
                        // 선택형(단일/복수)인 경우 기본 옵션 2개 생성, 서술형인 경우 옵션 비우기
                        options: (type === 'single' || type === 'multiple')
                            ? (q.options.length >= 2 ? q.options : [{ id: `o-${Date.now()}-1`, value: '' }, { id: `o-${Date.now()}-2`, value: '' }])
                            : [],
                    }
                    : q
            ),
        }));
    }, []);

    // 옵션 추가
    const addOption = useCallback((questionId: string) => {
        setTemplateFormData((prev) => ({
            ...prev,
            questions: prev.questions.map((q) =>
                q.id === questionId
                    ? { ...q, options: [...q.options, { id: `o-${Date.now()}`, value: '' }] }
                    : q
            ),
        }));
    }, []);

    // 옵션 삭제
    const deleteOption = useCallback((questionId: string, optionId: string) => {
        setTemplateFormData((prev) => ({
            ...prev,
            questions: prev.questions.map((q) =>
                q.id === questionId
                    ? { ...q, options: q.options.filter((o) => o.id !== optionId) }
                    : q
            ),
        }));
    }, []);

    // 옵션 수정
    const updateOption = useCallback((questionId: string, optionId: string, value: string) => {
        setTemplateFormData((prev) => ({
            ...prev,
            questions: prev.questions.map((q) =>
                q.id === questionId
                    ? {
                        ...q,
                        options: q.options.map((o) =>
                            o.id === optionId ? { ...o, value } : o
                        ),
                    }
                    : q
            ),
        }));
    }, []);

    // 폼 데이터 업데이트
    const updateFormData = useCallback((updates: Partial<TemplateFormData>) => {
        setTemplateFormData((prev) => ({ ...prev, ...updates }));
    }, []);

    // 뷰 모달용 템플릿 데이터 (선택된 버전 데이터 우선, 없으면 상세 데이터, 없으면 기본)
    const templateForView = useMemo(() => {
        if (!selectedTemplate) return null;

        // 선택된 버전이 있고 해당 버전 데이터가 로드되면 해당 데이터 사용
        if (selectedVersionForPreview && selectedVersionData) {
            const versionData = selectedVersionData as {
                versionNumber?: number;
                contentHtml?: string;
                questions?: Array<{
                    id?: number;
                    questionText?: string;
                    type?: 'TEXT' | 'SINGLE_CHOICE' | 'MULTI_CHOICE';
                    options?: string[];
                    required?: boolean;
                    displayOrder?: number;
                }>;
                fileUrl?: string;
                contentType?: string;
                changeNote?: string;
            };
            return {
                ...selectedTemplate,
                content: versionData.contentHtml || '',
                questions: (versionData.questions || []).map((q, idx) => ({
                    id: String(q.id || `temp-${idx}`),
                    name: q.questionText || '',
                    type: (q.type === 'SINGLE_CHOICE' ? 'single' : q.type === 'MULTI_CHOICE' ? 'multiple' : 'text') as QuestionType,
                    options: (q.options || []).map((opt, optIdx) => ({
                        id: `opt-${idx}-${optIdx}`,
                        value: opt,
                    })),
                    isRequired: q.required ?? false,
                })),
                isFileUploaded: !!versionData.fileUrl && versionData.contentType !== 'HTML',
                fileUrl: versionData.fileUrl,
                fileType: versionData.contentType === 'PDF' ? 'pdf' as const : versionData.contentType === 'IMAGE' ? 'image' as const : null,
                currentVersion: versionData.versionNumber || selectedVersionForPreview.version,
            } as ConsentTemplate;
        }

        // 기본 템플릿 상세 데이터 사용
        if (templateDetailData && (templateDetailData as ConsentTemplateResponse).id === selectedTemplate.id) {
            return mapApiTemplateDetailToLocal(templateDetailData as ConsentTemplateResponse);
        }
        return selectedTemplate;
    }, [selectedTemplate, templateDetailData, selectedVersionForPreview, selectedVersionData]);

    return {
        // State
        templates,
        isLoadingTemplates,
        isTemplateModalOpen,
        isViewModalOpen,
        isDeleteModalOpen,
        isVersionHistoryModalOpen,
        selectedTemplate,
        templateForView,
        templateModalMode,
        templateFormData,
        fileInputRef,
        sensors,
        selectedVersionForPreview,
        versionHistory,

        // Pagination
        pageInfo,
        goToPage,
        goToNextPage,
        goToPrevPage,
        goToFirstPage,
        goToLastPage,

        // Modal actions
        openCreateModal,
        openEditModal,
        openViewModal,
        openDeleteModal,
        openVersionHistoryModal,
        closeVersionHistoryModal,
        closeModals,

        // Template actions
        toggleTemplateMandatory,
        duplicateTemplate,
        deleteTemplate,
        saveTemplate,

        // Version management actions
        setActiveVersion,
        getVersionContent,
        setSelectedVersionForPreview,

        // File actions (PDF or Image)
        handleFileUpload,
        clearFileUpload,
        isUploading,

        // Question actions
        handleDragEnd,
        addQuestion,
        deleteQuestion,
        updateQuestion,
        changeQuestionType,
        addOption,
        deleteOption,
        updateOption,

        // Form actions
        updateFormData,
        setTemplateFormData,

        // Setters (for direct JSX control)
        setIsTemplateModalOpen,
        setIsViewModalOpen,
        setIsDeleteModalOpen,
        setIsVersionHistoryModalOpen,

        // Mutations status
        isCreating: createTemplateMutation.isPending,
        isUpdating: updateTemplateMutation.isPending,
        isDeleting: deleteTemplateMutation.isPending,
    };
}
